import { Flex, Icon as ChakraIcon, Text } from '@chakra-ui/react';
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

/** About V2 sections share the Thmanyah type ramp (see {@link HeroSection}). */
const FONT_FAMILY = "'Thmanyah Sans', 'Noto Sans Arabic', sans-serif";

/** Tinted surfaces for a highlighted pill, per the design. */
const TONE_SURFACE = {
  accent: '#E5D5F5', // lavender
  soft: '#FFE6E3', // coral-pink
} as const;

export type FeaturePillTone = keyof typeof TONE_SURFACE;

export interface FeaturePillProps {
  /** Leading icon (lucide). */
  icon: LucideIcon;
  /** Short label. */
  label: ReactNode;
  /** Accent background variant for the highlighted pill. */
  highlighted?: boolean;
  /** Which tinted surface to use when highlighted. Defaults to 'accent' (lavender). */
  tone?: FeaturePillTone;
}

/**
 * Feature pill (TZ-380): a rounded stadium pill with a label and a trailing
 * icon, used in the "ليش تكت زون؟" grid. RTL — the label leads (right) and the
 * icon sits on the trailing (left) side, per the design. Supports a default
 * (white, elevated) surface and a highlighted (accent) variant.
 */
export const FeaturePill = ({
  icon,
  label,
  highlighted = false,
  tone = 'accent',
}: FeaturePillProps) => (
  <Flex
    dir='rtl'
    align='center'
    justify='center'
    gap='12px'
    w='100%'
    minH='48px'
    px='16px'
    py='12px'
    borderRadius='9999px'
    bg={highlighted ? TONE_SURFACE[tone] : 'white'}
    borderStyle='solid'
    borderWidth='0.69px'
    borderColor={highlighted ? 'transparent' : '#F0EFEC'}
    boxShadow={
      highlighted ? 'none' : '0 1px 2px rgba(27, 25, 23, 0.06), 0 10px 24px rgba(27, 25, 23, 0.05)'
    }
  >
    <Text
      as='span'
      m={0}
      color='#1B1917' // text-primary
      fontFamily={FONT_FAMILY}
      fontWeight='500'
      fontSize='12px'
      lineHeight='140%'
      letterSpacing='0.01em'
      whiteSpace='nowrap'
    >
      {label}
    </Text>
    <ChakraIcon
      as={icon}
      color='#FF5547' // accent-coral
      boxSize={{ base: '18px', md: '20px' }}
      strokeWidth={2}
      flexShrink={0}
      aria-hidden
    />
  </Flex>
);
